/**
 * The header every hub page wears, except the front door.
 *
 * ⚠️ A COMPONENT, NOT A CLASS TO REMEMBER. The previous version was a
 * `<div className="page-band">` each page wrapped its own header in, applied
 * across six files by a script — which put the back link outside the wrapper on
 * two of them and banded a sent-state header on a third. All three compiled and
 * all three looked wrong. A component takes the title and renders the rest, so
 * there is nothing to get subtly out of step.
 *
 * ⚠️ AND THE TITLE SITS ON THE IMAGE HERE, WHICH AN EARLIER ATTEMPT COULD NOT
 * DO. That failed because the band was 190px of bright sunset and the text had
 * no ground under it. Given real depth and a gradient that genuinely darkens
 * the left, it reads — the earlier problem was contrast, not the idea.
 */
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HUB_MODULES, moduleById } from '@commpass/shared';

/**
 * The programme this page lives inside, if any.
 *
 * ⚠️ LOOKED UP FROM THE ROUTE, NOT PASSED IN BY EACH PAGE (Josh, 2026-09-05:
 * "if you're in Loss runs and you want to get back to the requests center, this
 * path should be commercial hub / requests center / loss runs"). The registry
 * already records that Loss Runs sits `within` the Requests Center, and it is
 * what generates the Requests Center's own buttons. Deriving the crumb from the
 * same field means a fourth request type gets a correct trail by declaring
 * itself, rather than by somebody remembering to pass a prop.
 */
function useParentCrumb(): { name: string; href: string } | null {
  const { pathname } = useLocation();
  const here = HUB_MODULES.find((m) => !m.external && m.href === pathname);
  const parent = here?.within ? moduleById(here.within) : undefined;
  return parent ? { name: parent.name, href: parent.href } : null;
}

export function PageHero({
  title,
  sub,
  action,
}: {
  title: string;
  /**
   * One line: what the page is for, in the member's terms.
   *
   * A node rather than a string because several of these are composed from
   * live values — a form count, an agency name, who answered a conversation.
   */
  sub?: ReactNode;
  /** The page's primary control, if it has one. */
  action?: ReactNode;
}) {
  const navigate = useNavigate();
  const parent = useParentCrumb();
  return (
    <header className="page-hero">
      <nav className="ph-crumbs" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate('/hub')}>
          Commercial Hub
        </button>
        <span aria-hidden="true">/</span>
        {parent && (
          <>
            <button type="button" onClick={() => navigate(parent.href)}>
              {parent.name}
            </button>
            <span aria-hidden="true">/</span>
          </>
        )}
        <b>{title}</b>
      </nav>
      <h1>{title}</h1>
      {sub && <p>{sub}</p>}
      {action && <div className="ph-action">{action}</div>}
    </header>
  );
}
